import * as React from "react";
import {inject} from "mobx-react";
import type {IViewColumn, IViewSort} from "../../../data/models/ViewUtils";
import {getSortForField, getSortIndex} from "../../../data/models/ViewUtils";
import {CheckboxInput} from "../input/checkbox/CheckboxInput";
import type {View} from "../../../data/models/View";
import type {IFieldPointer} from "../../../data/models/field/Field";
import type {Pointer} from "../../../utils/interaction/Pointer";
import {ReactUtils} from "../../utils/ReactUtils";
import type {XyiconFeature} from "../../../generated/api/base";
import type {AppState} from "../../../data/state/AppState";
import type {ManageColumnCallback} from "./TableHeaderDropDown";
import {TableHeader} from "./TableHeader";
import {minHeaderWidth} from "./TableConstants";
import type {ColumnDropIndicatorSide} from "./TableUtils";
import {getColumnDropIndicatorSide, getColumnDropIndicatorSideFromPosition, getColumnDropReorderIndex} from "./TableUtils";

interface ITableHeadersProps {
	readonly headers: Partial<IViewColumn>[];
	readonly checkboxColumn: boolean;
	readonly allSelected: boolean;
	readonly onAllClicked: () => void;
	readonly onToggleSort: (column: IFieldPointer) => void;
	readonly onAutoResizeColumn: (index: number, header: Partial<IViewColumn>) => void;
	readonly onReorderColumn?: (index: number, newIndex: number) => void;
	readonly onHeaderWidthChange: (index: number, width: number, header: Partial<IViewColumn>) => void;
	readonly onHeaderWidthPreview?: (index: number, width: number, header: Partial<IViewColumn>) => void;
	readonly headerRef: React.RefObject<HTMLDivElement>;
	readonly tableRowIcon?: boolean;
	readonly feature: XyiconFeature;
	readonly view: View;
	readonly onManageColumns?: ManageColumnCallback;
	readonly onSortAsc?: (id: IFieldPointer) => void;
	readonly onSortDesc?: (id: IFieldPointer) => void;
	readonly onClearSort?: (id: IFieldPointer) => void;
	readonly sorts: IViewSort[];
	readonly appState?: AppState;
}

interface ITableHeadersState {
	dragSourceIndex: number;
	dragTarget: number;
	dragTargetSide: ColumnDropIndicatorSide | null;
}

@inject("appState")
export class TableHeaders extends React.Component<ITableHeadersProps, ITableHeadersState> {
	private _columnDrags: number[] = [];
	private _columnDragWidths: number[] = [];
	private _columnDragMoved: boolean[] = [];

	constructor(props: ITableHeadersProps) {
		super(props);

		this.state = {
			dragSourceIndex: -1,
			dragTarget: -1,
			dragTargetSide: null,
		};
	}

	private onDragStart = (index: number, event: React.DragEvent) => {
		if (this.props.onReorderColumn) {
			this.setState({dragSourceIndex: index});
		}
	};

	private onDragEnd = (index: number, event: React.DragEvent) => {
		if (this.props.onReorderColumn) {
			const {dragSourceIndex, dragTarget, dragTargetSide} = this.state;

			this.setState({
				dragSourceIndex: -1,
				dragTarget: -1,
				dragTargetSide: null,
			});

			const newIndex = getColumnDropReorderIndex(dragSourceIndex, dragTarget, dragTargetSide);

			if (newIndex === null) {
				return;
			}

			this.props.onReorderColumn(dragSourceIndex, newIndex);
		}
	};

	private onDragEnter = (index: number, event: React.DragEvent) => {
		if (this.props.onReorderColumn) {
			this.setState({dragTarget: index, dragTargetSide: this.getDragTargetSide(event)});
		}
	};

	private onDragOver = (index: number, event: React.DragEvent) => {
		if (this.props.onReorderColumn) {
			event.preventDefault();
			this.setState({dragTarget: index, dragTargetSide: this.getDragTargetSide(event)});
		}
	};

	private onDragLeave = (index: number, event: React.DragEvent) => {
		if (this.props.onReorderColumn) {
			this.setState({dragTarget: -1, dragTargetSide: null});
		}
	};

	private getDragTargetSide = (event: React.DragEvent): ColumnDropIndicatorSide => {
		const bounds = (event.currentTarget as HTMLElement).getBoundingClientRect();

		return getColumnDropIndicatorSideFromPosition(event.clientX, bounds.left, bounds.width);
	};

	private onColResizeStart = (index: number, pointer: Pointer) => {
		this._columnDrags[index] = this.props.headers[index].width || minHeaderWidth;
		this._columnDragWidths[index] = this._columnDrags[index];
		this._columnDragMoved[index] = false;
	};

	private onColResizeMove = (index: number, pointer: Pointer, header: Partial<IViewColumn>) => {
		const width = Math.max(minHeaderWidth, this._columnDrags[index] + pointer.offsetX);

		this._columnDragWidths[index] = width;
		this._columnDragMoved[index] = true;

		if (this.props.onHeaderWidthPreview) {
			this.props.onHeaderWidthPreview(index, width, header);
		} else {
			this.props.onHeaderWidthChange(index, width, header);
		}
	};

	private onColResizeEnd = (index: number, pointer: Pointer, header: Partial<IViewColumn>) => {
		if (this.props.onHeaderWidthPreview && this._columnDragMoved[index]) {
			const width = this._columnDragWidths[index] ?? this._columnDrags[index] ?? minHeaderWidth;

			this.props.onHeaderWidthChange(index, width, header);
		}
	};

	public override render() {
		const {
			headers,
			checkboxColumn,
			allSelected,
			onAllClicked,
			tableRowIcon,
			feature,
			appState,
			view,
			onSortAsc,
			onSortDesc,
			onClearSort,
			sorts,
			headerRef,
			onToggleSort,
			onAutoResizeColumn,
			onManageColumns,
		} = this.props;
		const {dragSourceIndex, dragTarget, dragTargetSide} = this.state;
		const validDragTargetSide = getColumnDropIndicatorSide(dragSourceIndex, dragTarget, dragTargetSide);
		const multipleSorts = appState.actions.getSortsForFeature(view, feature).length > 1;

		return (
			<div
				ref={headerRef}
				className={ReactUtils.cls("head", {tableRowIcon})}
			>
				{checkboxColumn && (
					<div
						className="th"
						onClick={onAllClicked}
					>
						<CheckboxInput
							value={allSelected}
							disabled={true}
						/>
						<div className="resizer checkbox" />
					</div>
				)}
				{headers.map((header, index) => {
					const sortIndex = getSortIndex(view, header.field, appState, feature);
					const sort = getSortForField(sorts, header.field) || sorts[0];

					return (
						<TableHeader
							key={header.field}
							header={header}
							index={index}
							sortIndex={sortIndex}
							sort={sort}
							multipleSorts={multipleSorts}
							dragSource={index === dragSourceIndex}
							dragTarget={index === dragTarget && validDragTargetSide !== null}
							dragTargetSide={validDragTargetSide}
							onDragStart={this.onDragStart}
							onDragEnd={this.onDragEnd}
							onDragEnter={this.onDragEnter}
							onDragOver={this.onDragOver}
							onDragLeave={this.onDragLeave}
							onColResizeStart={this.onColResizeStart}
							onColResizeMove={this.onColResizeMove}
							onColResizeEnd={this.onColResizeEnd}
							onToggleSort={onToggleSort}
							onAutoResizeColumn={onAutoResizeColumn}
							onManageColumns={onManageColumns}
							allowHide={headers.length > 1}
							feature={feature}
							onSortAsc={onSortAsc}
							onSortDesc={onSortDesc}
							onClearSort={onClearSort}
						/>
					);
				})}
			</div>
		);
	}
}
